import { Box, Link, List, ListItem, Stack, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import { Icons, HXCIcon } from '../../HXCIcon';
import { Text } from '../../Text';
import { IconButton } from '../../IconButton';
import type { LocalNavProps } from '../LocalNav/LocalNav';
import type { MenuLinkData } from '../LocalLinks/LocalLinks';
import useGlobalNavLinks from '../GlobalNav/useGlobalNavLinks';

import type { MobileNavProps } from './MobileNav.types';

interface MainMenuContentProps extends MobileNavProps {
  onMenuClick: (menuLink: MenuLinkData) => void;
  toggleDrawer: (open: boolean) => void;
  localNavProps: LocalNavProps;
}

const localNavItemSx = { py: 0.5 } as const;

const localNavRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: 0,
  paddingLeft: 1,
  margin: 0,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left' as const,
  color: 'inherit',
  font: 'inherit',
  textDecoration: 'none',
};

const LocalNavLabel = ({ children }: { children: ReactNode }) => (
  <Text variant="p" color="silver" fontSize="1rem">
    {children}
  </Text>
);

const DrawerContent = ({
  activeLink,
  localLinksData,
  onMenuClick,
  toggleDrawer,
  localNavProps,
}: MainMenuContentProps) => {
  const theme = useTheme();
  const globalNavLinks = useGlobalNavLinks({ activeLink, isMobile: true });
  const showOrderHistoryLink = localNavProps.showOrderHistoryLink ?? true;
  const showIrelandPrompt = localNavProps.showIrelandPrompt ?? true;

  return (
    <Stack
      sx={{
        width: '300px',
        height: '100%',
        backgroundColor: theme.palette.black.main,
      }}
      gap={2}
    >
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        sx={{ padding: '8px 24px' }}
        alignItems={'center'}
        data-component="GlobalNavLinkMobile"
      >
        <Text
          color={'white'}
          variant="p"
          sx={{
            borderLeft: `2px solid ${theme.palette.white.main}`,
            paddingLeft: '16px',
            fontSize: '18px',
          }}
        >
          {globalNavLinks.filter((link) => link.active)[0].name}
        </Text>
        <IconButton
          iconName={Icons.CloseRg}
          onClick={() => toggleDrawer(false)}
          label="close"
          style={{
            padding: 0,
            minWidth: 0,
          }}
          iconColor={'white'}
        />
      </Stack>
      <List sx={{ paddingLeft: '5px' }}>
        {localLinksData?.map((link) => {
          if ('options' in link) {
            const menuLink = link;
            return (
              <ListItem key={menuLink.text} sx={localNavItemSx}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => onMenuClick(menuLink)}
                  sx={localNavRowSx}
                >
                  <LocalNavLabel>{menuLink.text}</LocalNavLabel>
                  <HXCIcon name={Icons.ArrowRightRg} color="white" sizePx={16} />
                </Box>
              </ListItem>
            );
          } else {
            const localLink = link;
            return (
              <ListItem
                key={localLink.href}
                data-component="LocalLinksLinkMobile"
                sx={localNavItemSx}
              >
                <Link
                  component={localNavProps.linkComponent}
                  key={localLink.href}
                  to={localLink.href}
                  underline="none"
                  sx={localNavRowSx}
                >
                  <LocalNavLabel>{localLink.text}</LocalNavLabel>
                </Link>
              </ListItem>
            );
          }
        })}

        {showOrderHistoryLink && (
          <ListItem data-component="OrderHistoryLinkMobile" sx={{ paddingY: 0.5 }}>
            <Link
              component={localNavProps.linkComponent}
              to="/account/order-history"
              underline="none"
              sx={{ paddingLeft: 1 }}
            >
              <Text variant="p" color="silver" fontSize="1rem">
                My Orders
              </Text>
            </Link>
          </ListItem>
        )}
      </List>
      {localNavProps?.buttons}
      <List>
        {globalNavLinks
          // we remove software because it has CC and CP links instead
          .filter((link) => !link.active && link.name !== 'Software')
          .map((link) => (
            <ListItem key={link.name}>
              <Link href={link.href} underline="none">
                <Text
                  variant="p"
                  color="white"
                  sx={{
                    fontSize: '18px',
                    borderLeft: `2px solid ${theme.palette.darkGray.main}`,
                    paddingLeft: '16px',
                  }}
                >
                  {link.name}
                </Text>
              </Link>
            </ListItem>
          ))}
      </List>
      {showIrelandPrompt && (
        <Stack
          bgcolor={theme.palette.header.globalNavBackground}
          mx={2}
          p={2}
          gap={1}
          flexDirection={'column'}
          borderRadius={2}
        >
          <Stack flexDirection={'row'} gap={1.5}>
            <HXCIcon name={'flagUK'} />
            <Text variant="p" color="white">
              You're on our UK website.
            </Text>
          </Stack>

          <Text variant="p" color="white">
            Looking for Healthxchange Ireland?{' '}
            <Link href={'https://healthxchange.ie/'}>Switch here</Link>
          </Text>
        </Stack>
      )}
    </Stack>
  );
};

export default DrawerContent;
